'use strict';

const CryptoEngine = require('./CryptoEngine');
const KeyPath = require('./KeyPath');
const Logger = require('./Logger');

/**
 * Názov kľúča, pod ktorým sa ukladá heslo (bez pepper) do výstupu.
 * Používa sa v encryptByKeys/encryptAll (addPass) a odstraňuje sa v decryptObject.
 *
 * @private
 * @type {string}
 */
let _crpKey = 'crp';

/**
 * Vysokoúrovňové API pre šifrovanie a dešifrovanie hodnôt v JSON konfiguráciách.
 *
 * Podporuje:
 * - Šifrovanie vybraných kľúčov (dot notácia) v celom objekte
 * - Rekurzívne dešifrovanie všetkých zašifrovaných hodnôt
 * - Bezpečný passthrough pre nezašifrované objekty
 * - Zachovanie pôvodných typov (string, number, boolean, null)
 *
 * @class
 * @example
 * const { ConfigCrypto } = require('@soitron/json-value-crypto');
 *
 * const cc = new ConfigCrypto({ password: process.env.CONFIG_SECRET });
 *
 * // Dešifrovanie celého objektu
 * const config = cc.decryptObject(zašifrovanýObjekt);
 *
 * // Dešifrovanie jednej hodnoty
 * const heslo = cc.decryptValue(zašifrovanýObjekt.db.password);
 */
class ConfigCrypto {
  /**
   * Vytvorí novú inštanciu ConfigCrypto.
   *
   * @param {Object} options - Konfigurácia
   * @param {string} options.password - Heslo pre šifrovanie/dešifrovanie
   * @throws {Error} Ak heslo nie je zadané
   */
  constructor({ password }) {
    /**
     * Heslo používateľa (bez pepperu).
     * Uložené pre prípad pridania do výstupu cez addPass.
     * @private
     * @type {string}
     */
    this._password = password;

    /**
     * Inštancia nízkoúrovňového šifrovacieho engine.
     * @private
     * @type {CryptoEngine}
     */
    this._engine = new CryptoEngine({ password });
  }

  /**
   * Nastaví názov kľúča pre ukladanie hesla do výstupu.
   *
   * @static
   * @param {string} key - Nový názov kľúča
   * @throws {Error} Ak kľúč nie je neprázdny reťazec
   */
  static setCrpKey(key) {
    if (!key || typeof key !== 'string') {
      throw new Error('CRP kľúč musí byť neprázdny reťazec.');
    }
    _crpKey = key;
  }

  /**
   * Vráti aktuálny názov kľúča pre ukladanie hesla.
   *
   * @static
   * @returns {string} Aktuálny názov kľúča
   */
  static getCrpKey() {
    return _crpKey;
  }

  /**
   * Zašifruje vybrané kľúče v JSON objekte.
   *
   * Pre každý kľúč (dot notácia):
   * - String/Number/Boolean/Null → zašifruje sa priamo
   * - Objekt → rekurzívne sa zašifrujú všetky leaf hodnoty
   * - Pole → zašifrujú sa všetky primitívne itemy
   *
   * @param {Object} obj - Zdrojový JSON objekt
   * @param {string[]} keyPaths - Cesty na šifrovanie v dot notácii
   * @param {Object} [options] - Voliteľné nastavenia
   * @param {boolean} [options.addPass] - Ak true, pridá kľúč "crp" s heslom (bez pepper) do výstupu
   * @returns {Object} Hlboká kópia objektu so zašifrovanými hodnotami
   * @throws {Error} Ak kľúč neexistuje v objekte
   */
  encryptByKeys(obj, keyPaths, options) {
    Logger.verbose('[ConfigCrypto]', `Šifrujem ${keyPaths.length} kľúčov.`);
    const result = ConfigCrypto._deepClone(obj);

    for (const path of keyPaths) {
      const value = KeyPath.get(result, path);
      if (value === undefined) {
        throw new Error(`Kľúč "${path}" nebol nájdený v objekte. ` + 'Skontrolujte cestu v dot notácii.');
      }
      Logger.verbose('[ConfigCrypto]', `Šifrujem cestu: ${path}`);
      const encrypted = this._encryptDeep(value, path);
      KeyPath.set(result, path, encrypted);
    }

    if (options && options.addPass === true) {
      Logger.verbose('[ConfigCrypto]', `Pridávam kľúč "${_crpKey}" s heslom do výstupu.`);
      result[_crpKey] = this._password;
    }

    return result;
  }

  /**
   * Zašifruje všetky leaf hodnoty v celom JSON objekte.
   *
   * Rekurzívne prejde celú štruktúru a zašifruje každú
   * primitívnu hodnotu (string, number, boolean, null).
   *
   * @param {Object} obj - Zdrojový JSON objekt
   * @param {Object} [options] - Voliteľné nastavenia
   * @param {boolean} [options.addPass] - Ak true, pridá kľúč "crp" s heslom (bez pepper) do výstupu
   * @returns {Object} Hlboká kópia objektu so všetkými hodnotami zašifrovanými
   */
  encryptAll(obj, options) {
    Logger.verbose('[ConfigCrypto]', 'Šifrujem všetky leaf hodnoty.');
    const result = this._encryptDeep(ConfigCrypto._deepClone(obj), '');

    if (options?.addPass === true) {
      Logger.verbose('[ConfigCrypto]', `Pridávam kľúč "${_crpKey}" s heslom do výstupu.`);
      result[_crpKey] = this._password;
    }

    return result;
  }

  /**
   * Rekurzívne zašifruje hodnotu podľa jej typu.
   *
   * @private
   * @param {*} value - Hodnota na zašifrovanie
   * @param {string} [currentPath=''] - Aktuálna cesta pre verbose log
   * @returns {*} Zašifrovaná hodnota alebo objekt so zašifrovanými leaf hodnotami
   */
  _encryptDeep(value, currentPath = '') {
    /* Už zašifrovaný payload — preskočíme (ochrana pred dvojitým šifrovaním) */
    if (CryptoEngine.isEncryptedPayload(value)) {
      Logger.verbose('[ConfigCrypto]', `  ⊘ ${currentPath || '(root)'} → už zašifrované, preskakujem`);
      return value;
    }

    /* Primitívne typy — šifrujeme priamo */
    if (value === null || typeof value !== 'object') {
      const typ = value === null ? 'null' : typeof value;
      Logger.verbose('[ConfigCrypto]', `  ✓ ${currentPath || '(root)'} (${typ}) → šifrujem`);
      return this._engine.encrypt(value);
    }

    /* Pole — šifrujeme každý item */
    if (Array.isArray(value)) {
      return value.map((item, i) => {
        const itemPath = currentPath ? `${currentPath}.${i}` : `${i}`;
        return this._encryptDeep(item, itemPath);
      });
    }

    /* Objekt — rekurzívne šifrujeme leaf hodnoty */
    const result = {};
    for (const [key, val] of Object.entries(value)) {
      const keyPath = currentPath ? `${currentPath}.${key}` : key;
      result[key] = this._encryptDeep(val, keyPath);
    }
    return result;
  }

  /**
   * Dešifruje jednu hodnotu.
   *
   * Ak hodnota nie je zašifrovaný payload ($enc), vráti ju bez zmeny.
   * Bezpečné pre volanie na ľubovoľnej hodnote — nikdy nespadne
   * na nezašifrovanom vstupe.
   *
   * @param {*} value - Zašifrovaný payload alebo ľubovoľná hodnota
   * @returns {*} Dešifrovaná hodnota alebo pôvodná hodnota
   */
  decryptValue(value) {
    if (CryptoEngine.isEncryptedPayload(value)) {
      Logger.verbose('[ConfigCrypto]', 'Dešifrujem jednu hodnotu.');
      return this._engine.decrypt(value);
    }
    Logger.verbose('[ConfigCrypto]', 'Hodnota nie je zašifrovaná — passthrough.');
    return value;
  }

  /**
   * Rekurzívne dešifruje všetky zašifrované hodnoty v objekte.
   *
   * Prechádza celý objekt a na každom $enc payload zavolá decrypt.
   * Nezašifrované hodnoty ponechá bez zmeny. Bezpečné pre volanie
   * na úplne nezašifrovanom objekte — vráti identickú kópiu.
   *
   * Ak vstupný objekt obsahoval aspoň jeden $enc payload a zároveň
   * obsahuje kľúč "crp", tento kľúč sa z výstupu automaticky odstráni.
   * Ak objekt nebol šifrovaný (žiadny $enc), kľúč "crp" zostáva —
   * mohla by to byť legitímna property používateľa.
   *
   * @param {Object} obj - Objekt (zašifrovaný, čiastočne zašifrovaný, alebo čistý)
   * @returns {Object} Hlboká kópia objektu s dešifrovanými hodnotami
   */
  decryptObject(obj) {
    Logger.verbose('[ConfigCrypto]', 'Dešifrujem objekt.');

    /* 1. Spočítaj $enc payloady pred dešifrovaním */
    const encCount = ConfigCrypto._countEncPayloads(obj);

    /* 2. Dešifruj */
    const result = this._decryptDeep(obj, '');

    /* 3. Odstráň crp LEN ak objekt bol naozaj šifrovaný */
    if (encCount > 0 && result !== null && typeof result === 'object' && !Array.isArray(result) && _crpKey in result) {
      Logger.verbose('[ConfigCrypto]', `Odstraňujem kľúč "${_crpKey}" z dešifrovaného výstupu.`);
      delete result[_crpKey];
    }

    return result;
  }

  /**
   * Rekurzívne spočíta $enc payloady v JSON štruktúre.
   *
   * @private
   * @static
   * @param {*} obj - JSON hodnota
   * @returns {number} Počet $enc objektov
   */
  static _countEncPayloads(obj) {
    if (obj === null || typeof obj !== 'object') return 0;
    if (CryptoEngine.isEncryptedPayload(obj)) return 1;
    let count = 0;
    for (const val of Object.values(obj)) {
      count += ConfigCrypto._countEncPayloads(val);
    }
    return count;
  }

  /**
   * Rekurzívne dešifruje hodnotu podľa jej typu.
   *
   * @private
   * @param {*} value - Hodnota na dešifrovanie
   * @param {string} [currentPath=''] - Aktuálna cesta pre verbose log
   * @returns {*} Dešifrovaná hodnota
   */
  _decryptDeep(value, currentPath = '') {
    /* Null alebo primitív — vrátime ako je */
    if (value === null || typeof value !== 'object') {
      return value;
    }

    /* Zašifrovaný payload — dešifrujeme */
    if (CryptoEngine.isEncryptedPayload(value)) {
      const result = this._engine.decrypt(value);
      const typ = result === null ? 'null' : typeof result;
      Logger.verbose('[ConfigCrypto]', `  ✓ ${currentPath || '(root)'} → dešifrované (${typ})`);
      return result;
    }

    /* Pole — rekurzívne spracovanie */
    if (Array.isArray(value)) {
      return value.map((item, i) => {
        const itemPath = currentPath ? `${currentPath}.${i}` : `${i}`;
        return this._decryptDeep(item, itemPath);
      });
    }

    /* Bežný objekt — rekurzívne spracovanie */
    const result = {};
    for (const [key, val] of Object.entries(value)) {
      const keyPath = currentPath ? `${currentPath}.${key}` : key;
      result[key] = this._decryptDeep(val, keyPath);
    }
    return result;
  }

  /**
   * Vytvorí hlbokú kópiu objektu cez JSON serializáciu.
   *
   * @private
   * @static
   * @param {Object} obj - Zdrojový objekt
   * @returns {Object} Hlboká kópia
   */
  static _deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
  }
}

module.exports = ConfigCrypto;
